import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { subscriptionsService } from "../features/subscriptions/service";

type Phase = "polling" | "success" | "timeout" | "mobile_done";

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const isMobile = params.get("source") === "mobile";

  const [phase, setPhase] = useState<Phase>(isMobile ? "mobile_done" : "polling");
  const [tier, setTier] = useState("");
  const attempts = useRef(0);
  const MAX_ATTEMPTS = 10; // 20s at 2s interval — after confirm-stripe, webhook is just a fallback

  useEffect(() => {
    if (isMobile) return;
    let stopped = false;

    async function activate() {
      // Step 1: immediately activate via confirm-stripe (no webhook wait needed).
      if (sessionId) {
        try {
          const result = await subscriptionsService.confirmStripeCheckout(sessionId);
          if (!stopped && (result.status === "active" || result.status === "trialing")) {
            setTier(result.tier || "");
            setPhase("success");
            return;
          }
        } catch {
          // confirm failed (session not paid yet, or other error) — fall through to polling
        }
      }

      // Step 2: poll the gateway-agnostic verify endpoint. Unlike a plain /me
      // read, this actively queries the provider (Razorpay/Cashfree/Stripe) and
      // activates the block — so Cashfree/Razorpay activate here without waiting
      // for a webhook (which never arrives in local/dev). Webhook stays a backup.
      while (!stopped && attempts.current < MAX_ATTEMPTS) {
        try {
          const sub = await subscriptionsService.verifyStatus();
          if (sub.active || sub.status === "active" || sub.status === "trialing") {
            if (!stopped) {
              setTier(sub.tier || "");
              setPhase("success");
            }
            return;
          }
        } catch {
          // ignore transient errors (provider_unreachable, not-yet-paid, etc.)
        }
        attempts.current += 1;
        await new Promise(r => setTimeout(r, 2000));
      }
      if (!stopped) setPhase("timeout");
    }

    void activate();
    return () => { stopped = true; };
  }, [sessionId, isMobile]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm max-w-sm w-full p-8 text-center">

        {phase === "mobile_done" && (
          <>
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">Payment Complete!</h1>
            <p className="text-sm text-gray-500 mb-6">Your mandate has been authorised. Return to the app to continue.</p>
          </>
        )}

        {phase === "polling" && (
          <>
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h1 className="text-lg font-bold text-gray-900 mb-1">Activating your plan…</h1>
            <p className="text-sm text-gray-500">Payment confirmed. Setting up your subscription.</p>
          </>
        )}

        {phase === "success" && (
          <>
            <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">You're all set!</h1>
            {tier && (
              <p className="text-sm font-semibold text-blue-600 capitalize mb-2">{tier} plan activated</p>
            )}
            <p className="text-sm text-gray-500 mb-6">Your subscription is now active. Enjoy all your plan features.</p>
            <button
              onClick={() => navigate("/subscription")}
              className="w-full py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
            >
              View my plan
            </button>
            <button
              onClick={() => navigate("/")}
              className="w-full mt-2 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Go to home
            </button>
          </>
        )}

        {phase === "timeout" && (
          <>
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h1 className="text-lg font-bold text-gray-900 mb-1">Payment received</h1>
            <p className="text-sm text-gray-500 mb-2">
              Your payment was successful. Subscription activation may take a moment — check back in a few seconds.
            </p>
            {sessionId && (
              <p className="text-[11px] text-gray-400 mb-4">Session: {sessionId}</p>
            )}
            <button
              onClick={() => { attempts.current = 0; setPhase("polling"); }}
              className="w-full py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors mb-2"
            >
              Check again
            </button>
            <button
              onClick={() => navigate("/subscription")}
              className="w-full py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              Go to subscription
            </button>
          </>
        )}
      </div>
    </div>
  );
}
